/**
 * The second column (PM efb0e798): the chosen section's pages, with the
 * current page highlighted. Sub-headings come from nested groups
 * (Broadcasts, Posts, Training) and categories in navConfig.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  getCardBackground,
  getLightBorder,
  getMutedText,
  getPrimaryText,
  getSecondaryText,
} from "../../../utils/themeUtils";
import { RailSection } from "./railModel";

interface SectionNavProps {
  section: RailSection;
  /** the page the current route belongs to */
  activeHref: string | null;
  isDarkMode: boolean;
  onNavigate: () => void;
  className?: string;
}

const SectionNav: React.FC<SectionNavProps> = ({
  section,
  activeHref,
  isDarkMode,
  onNavigate,
  className = "",
}) => (
  <div
    className={`h-full w-56 flex-shrink-0 overflow-y-auto border-r ${getCardBackground(
      isDarkMode
    )} ${getLightBorder(isDarkMode)} ${className}`}
    data-testid="section-nav"
  >
    <div className="px-2 pb-4">
      {section.groupLabel && (
        <div className={`px-3 pt-4 text-[11px] font-semibold uppercase tracking-wider ${getMutedText(isDarkMode)}`}>
          {section.groupLabel}
        </div>
      )}
      <h2
        className={`px-3 pb-2 ${section.groupLabel ? "pt-1" : "pt-4"} text-sm font-semibold ${getPrimaryText(
          isDarkMode
        )}`}
      >
        {section.title}
      </h2>
      {section.pageGroups.map((group, gi) => (
        <div key={`${group.label ?? "_"}-${gi}`} className={group.label ? "mt-2" : ""}>
          {group.label && (
            <div className={`px-3 pt-1 pb-1 text-xs font-semibold ${getMutedText(isDarkMode)}`}>
              {group.label}
            </div>
          )}
          <ul className="space-y-0.5">
            {group.pages.map((page) => {
              const active = activeHref === page.to;
              return (
                <li key={page.to}>
                  <Link
                    to={page.to}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                      group.label ? "pl-5" : ""
                    } ${
                      active
                        ? // Navigation selection is the one place the club's color
                          // shows (Dan 2026-10-31): a bar at the left edge, not a fill.
                          `font-semibold shadow-[inset_3px_0_0_var(--color-primary)] ${
                            isDarkMode ? "bg-gray-700/60 text-white" : "bg-gray-100 text-gray-900"
                          }`
                        : `${getSecondaryText(isDarkMode)} ${
                            isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                          }`
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate">{page.label}</span>
                    {page.comingSoon && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                        Soon
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

export default SectionNav;
